import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { formatMoney, formatDate, formatDateRange } from "@/lib/api";
import { displayFor } from "@/lib/bills-util";
import { projectIncomeEvents } from "@/lib/income-projection";
import StatusBadge from "@/components/StatusBadge";
import { MiniRow } from "./shared";

// "Your next three months" — renders when the Dashboard toggle is set to
// Month view. Shows the NEXT three calendar months only (never the current
// month, since the current-month view lives above in Steps 1-3). Uses the
// same visual language as pay-period and next-30-day cards: paycheck rows
// with amounts (clickable to edit / mark received), a bills-due row, and a
// projected end-of-month balance.
//
// All numbers are frontend-projected via the shared income-projection
// helper so the receive/paid state stays in sync across every Dashboard
// mode (fixed 2026-02: received paychecks used to reappear as projected
// income on Month view).
export default function NextMonthsStrip({
  monthlyForecast,     // list of month meta objects from /api/forecast/monthly
                       //   [ {month_start, month_end, month_label, status, extra_paycheck, ...}, ... ]
  bills = [],
  incomes = [],
  fundingSources = [],
  onOpenIncomeEvent,
}) {
  const today = new Date(); today.setHours(0, 0, 0, 0);

  // Sum of every "spending" source's manually-entered balance (checking,
  // savings, cash). Matches the pay-period card's starting bank total.
  const startingBalance = fundingSources
    .filter((f) => f.contributes_to_total_balance && f.current_source_balance_optional != null)
    .reduce((s, f) => s + Number(f.current_source_balance_optional || 0), 0);

  // Skip the current month — Dashboard already shows this month's numbers
  // above; this strip is strictly forward-looking. Take up to 3 future months.
  const currentMonthIso = today.toISOString().slice(0, 7);
  const futureMonths = (monthlyForecast || [])
    .filter((m) => (m.month_start || "").slice(0, 7) !== currentMonthIso)
    .slice(0, 3);

  // Cumulative end-of-month balance: month N's start = month N-1's end.
  let running = startingBalance;
  const cards = futureMonths.map((m) => {
    const start = new Date(m.month_start + "T00:00:00");
    const end = new Date(m.month_end + "T00:00:00");
    const incomeEvents = projectIncomeEvents(incomes, start, end);
    const incomeTotal = incomeEvents.reduce((s, e) => s + Number(e.amount || 0), 0);
    const monthBills = bills.filter((b) => {
      const d = new Date(b.due_date + "T00:00:00");
      if (d < start || d > end) return false;
      if (b.status === "cleared") return false;
      if (b.status === "paid" && !displayFor(b, today).notCleared) return false;
      return true;
    });
    const billsTotal = monthBills.reduce((s, b) => s + Number(b.estimated_due_amount || 0), 0);
    const netFlow = incomeTotal - billsTotal;
    const startBal = running;
    const endBal = startBal + netFlow;
    running = endBal;
    return {
      meta: m,
      start: m.month_start,
      end: m.month_end,
      incomes: incomeEvents,
      incomeTotal,
      billsCount: monthBills.length,
      billsTotal,
      netFlow,
      startBalance: startBal,
      endBalance: endBal,
    };
  });

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-[#8A918D]">Ahead</div>
          <h2 className="font-heading text-xl font-medium text-[#2C302E]">Your next three months</h2>
          <p className="text-xs text-[#59605C] mt-1">The three calendar months after this one. Each card shows every scheduled paycheck (click any to edit or mark received), the bills due, and the projected balance at month-end.</p>
        </div>
        <Link to="/app/planning" className="btn-ghost text-sm" data-testid="dashboard-months-link">See planner →</Link>
      </div>
      {cards.length === 0 ? (
        <p className="text-sm text-[#59605C]" data-testid="dashboard-months-empty">No monthly forecast yet — add a recurring income to see what&rsquo;s ahead.</p>
      ) : (
        <div className="grid md:grid-cols-3 gap-4" data-testid="dashboard-months-grid">
          {cards.map((c, i) => {
            const statusPill = c.meta.status || "safe";
            return (
              <div
                key={i}
                className="p-4 rounded-2xl bg-[#F9F8F6] border-2 border-[#D8CFB9]"
                data-testid={`dashboard-month-${i}`}
              >
                <StatusBadge status={statusPill} size="sm" />
                <div className="mt-3 text-[10px] font-semibold uppercase tracking-widest text-[#8A918D]">Month</div>
                <div className="font-heading text-base text-[#2C302E] leading-snug" data-testid={`dashboard-month-label-${i}`}>
                  {c.meta.month_label}
                </div>
                <div className="text-[11px] text-[#8A918D] mt-0.5">{formatDateRange(c.start, c.end)}</div>
                {c.incomes.length > 0 && (
                  <div className="mt-3">
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-[#3B5B3E] mb-0.5">Scheduled income</div>
                    <div className="space-y-0.5">
                      {c.incomes.map((inc, j) => (
                        <button
                          type="button"
                          key={j}
                          className="w-full flex justify-between text-xs hover:bg-[#F1EEE7] rounded px-1 -mx-1 py-0.5 transition-colors"
                          onClick={() => onOpenIncomeEvent && onOpenIncomeEvent({
                            income_id: inc.income_id,
                            source: inc.source,
                            amount: Number(inc.amount || 0),
                            date: inc.date,
                            frequency: inc.frequency,
                          })}
                          title="Edit this occurrence or the whole recurring source"
                          data-testid={`dashboard-month-inc-edit-${i}-${j}`}
                        >
                          <span className="text-[#59605C] truncate mr-2 underline decoration-dotted underline-offset-2">
                            {inc.source} · {formatDate(inc.date)}
                          </span>
                          <span className="text-[#3B5B3E]">+{formatMoney(inc.amount)}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {c.meta.extra_paycheck && (
                  <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#FEF3C7] text-[#7A4E1F] border border-[#F0DDA0]" title={`This month has ${c.incomes.length} paydays`}>
                    <span aria-hidden="true">✨</span> Extra paycheck month
                  </div>
                )}
                <div className="mt-4 space-y-1 text-sm border-t border-[#EBE6E0] pt-3">
                  <MiniRow label="Total scheduled income" value={`+${formatMoney(c.incomeTotal)}`} pos />
                  <Link
                    to={`/app/bills?from=${c.start}&to=${c.end}`}
                    className="flex justify-between text-sm text-[#59605C] hover:text-[#4A6148] hover:underline decoration-dotted underline-offset-2 focus:outline-none"
                    title="See every bill due in this month"
                    data-testid={`dashboard-month-bills-${i}`}
                  >
                    <span>{`Bills due (${c.billsCount})`}</span>
                    <span className="text-[#8C3A30]">-{formatMoney(c.billsTotal)}</span>
                  </Link>
                  {/* Per-month DELTA — how does this month stand on its own?
                      Not a compounding running total. Prior-month balances
                      are intentionally not carried forward here so each card
                      answers "will income cover bills this month?". The
                      compounded running total still lives on the Monthly
                      Outlook page for users who want the multi-month view. */}
                  <div className="pt-2 mt-1 border-t border-[#EBE6E0] flex justify-between font-semibold text-[#2C302E]" data-testid={`dashboard-month-balance-${i}`}>
                    <span>Net for this month</span>
                    <span className={c.netFlow < 0 ? "text-[#8C3A30]" : "text-[#3B5B3E]"}>
                      {c.netFlow >= 0 ? "+" : ""}{formatMoney(c.netFlow)}
                    </span>
                  </div>
                  <Link
                    to="/app/income?add=1"
                    className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-[#EEF3EC] text-[#3B5B3E] hover:bg-[#D5E1D3] transition-colors"
                    title="Add an expected paycheck or one-time deposit"
                    data-testid={`dashboard-month-add-income-${i}`}
                  >
                    <Plus size={11} /> Add expected income
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
